import axiosInstance from '../helpers/axios';
import {
  TOpsCommsStatus,
  TOpsMessageCategory,
  TOpsOutboxAction,
  TOpsOutboxList,
  TOpsQueueBody,
  TOpsQueueResult,
} from '../types';

/** Mode, transport, from-address and safety rails — for the status strip. */
export const getOpsCommsStatus = async () => {
  const response = await axiosInstance.get<TOpsCommsStatus>('ops/comms/status');
  return response.data;
};

export const queueOpsMessage = async (body: TOpsQueueBody) => {
  const response = await axiosInstance.post<TOpsQueueResult>(
    'ops/comms/queue',
    body,
  );
  return response.data;
};

export const getOpsOutbox = async (params: {
  page?: number;
  recordId?: string;
  status?: string;
}) => {
  const response = await axiosInstance.get<TOpsOutboxList>('ops/comms/outbox', {
    params,
  });
  return response.data;
};

/** Release a held message or retry a failed one (live mode only). */
export const sendOpsMessage = async (id: string) => {
  const response = await axiosInstance.post<TOpsOutboxAction>(
    `ops/comms/outbox/${id}/send`,
  );
  return response.data;
};

export const cancelOpsMessage = async (id: string) => {
  const response = await axiosInstance.post<{ id: string; status: string }>(
    `ops/comms/outbox/${id}/cancel`,
  );
  return response.data;
};

/** A string sets the local override; an explicit null clears it. */
export const patchOpsTemplate = async (
  id: string,
  body: { subject?: string | null; body?: string | null },
) => {
  const response = await axiosInstance.patch<{ id: string; reverted: boolean }>(
    `ops/comms/templates/${id}`,
    body,
  );
  return response.data;
};

// ── Automations (triggered sends) ──────────────────────────────────────────

export type TOpsAutomationCounts = {
  due: number;
  queued: number;
  suppressed: number;
  skipped: number;
  'already-queued': number;
  'would-queue': number;
  failed: number;
};

export type TOpsAutomationRule = {
  key: string;
  description: string;
  cron: string;
  category: TOpsMessageCategory;
  /** Schedule held by AUTOMATION_PAUSED_RULES; Preview and Run now still work. */
  paused: boolean;
  template: { id: string | null; name: string } | null;
  templateFallback: boolean;
  dueCount: number | null;
  dueSkipped: number | null;
  dueError: string | null;
  note: string | null;
  lastRun: {
    id: string;
    at: string;
    preview: boolean;
    trigger: 'cron' | 'manual';
    counts: TOpsAutomationCounts;
    error: string | null;
  } | null;
};

export type TOpsAutomationList = {
  mode: 'off' | 'dry-run' | 'live';
  schedulerTo: string;
  sendMode: 'dry-run' | 'live';
  pausedRules: string[];
  rules: TOpsAutomationRule[];
};

export type TOpsAutomationRunItem = {
  recordId: string;
  recordObject: string;
  label: string;
  bucket: string;
  to: string[];
  templateName: string | null;
  idempotencyKey: string;
  outcome:
    | 'queued'
    | 'suppressed'
    | 'skipped'
    | 'already-queued'
    | 'would-queue'
    | 'failed';
  reason: string | null;
  outboxId: string | null;
};

export type TOpsAutomationRun = {
  id: string;
  key: string;
  ran: boolean;
  reason?: string;
  at: string;
  durationMs: number;
  mode: 'off' | 'dry-run' | 'live';
  preview: boolean;
  counts: TOpsAutomationCounts;
  error: string | null;
  items: TOpsAutomationRunItem[];
  itemsTruncated: number;
};

/** Rules, mode, last run per rule and the due-now counts. */
export const getOpsAutomations = async (opts?: { due?: boolean }) => {
  const response = await axiosInstance.get<TOpsAutomationList>(
    'ops/comms/automations',
    { params: opts?.due === false ? { due: 0 } : undefined },
  );
  return response.data;
};

/** Run one rule now; `preview` reports what would go without queueing. */
export const runOpsAutomation = async (key: string, preview: boolean) => {
  const response = await axiosInstance.post<TOpsAutomationRun>(
    `ops/comms/automations/${key}/run`,
    { preview },
  );
  return response.data;
};
